import { CacheKeys, ErrorBoundary, useSessionStorage } from '@bechtel/client-common';
import { Carousel } from '@bechtel/client-dashboard-common';
import { WeldDrawerValuesProps } from '@bechtel/client-welds';
import { aboveBreakpoint, Spinner, SpinnerSize, Theme, useMediaQuery } from '@bechtel/gds';
import { CommodityStats, CrewMember } from '@bechtel/types';
import { get } from 'lodash';
import { FC, Suspense, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeContext } from 'styled-components';
import { CommodityStatsSection } from '~/commodity-stats';
import { MapMarkers } from '~/components/organisms/map-markers';
import { useCommodityFitupStatsFWEQueryResult, useWeeklyCommodityQueryResult, useWeldTypeId } from '~/hooks';
import { SearchBar } from '~/search-bar';
import { WeldFilterInput } from '~/search-bar/types';
import {
  CarouselContainerStyled,
  DashboardPersonaContentStyled,
  MapContainerStyled,
  WeldSectionListStyled,
} from '~/styled';
import { WeldTabs } from '~/weld-tabs';
import { FieldEngineerDashboardStatStyled, FieldEngineerLeftSectionStyled } from './styled';

interface FieldEngineerDashboardContentProps {
  crewMember: CrewMember;
  enableProjectMap?: boolean;
}
interface NavigateToWeldProps {
  weldDrawerValuesProps: WeldDrawerValuesProps;
  applyBlockedFilter?: string;
  isWeeklyStats?: boolean;
}
export const FieldEngineerDashboardContent: FC<FieldEngineerDashboardContentProps> = ({
  crewMember,
  enableProjectMap,
}) => {
  const [showMap, setShowMap] = useState<boolean>(true);
  const navigate = useNavigate();
  const { breakpoints } = useContext<Theme>(ThemeContext);
  const [weldFilter, setWeldFilter] = useState<WeldFilterInput>();
  const isWideScreen = useMediaQuery(aboveBreakpoint(breakpoints.widescreen));
  const setCursor = useSessionStorage<number>(CacheKeys.WeldListCursor)[1];
  const setDrawerValues = useSessionStorage<WeldDrawerValuesProps>(CacheKeys.WeldDrawerValues)[1];
  const { weldTypeId } = useWeldTypeId();
  const { weeklyCommodityStatsError, weeklyCommodityStatsData, weeklyCommodityStatsLoading, weeklyRefetch } =
    useWeeklyCommodityQueryResult(weldTypeId, crewMember, weldFilter);

  const { fitupStatsError, fitupStatsData, fitupLoading, fitUpRefetch } = useCommodityFitupStatsFWEQueryResult(
    weldTypeId,
    crewMember,
    weldFilter
  );
  const weeklyStatusStats: CommodityStats[] = get(
    weeklyCommodityStatsData?.commodityStats,
    'commodityStats.statusStats',
    []
  );

  const fitupStatusStats = get(fitupStatsData?.commodityStats, 'commodityStats.statusStats');

  const navigateToWeld = async (navigateToWeldProps: NavigateToWeldProps) => {
    setCursor(1);
    await setDrawerValues(navigateToWeldProps?.weldDrawerValuesProps);

    if (navigateToWeldProps?.isWeeklyStats || navigateToWeldProps?.applyBlockedFilter) {
      navigate('/', {
        state: {
          applyBlockedFilter: navigateToWeldProps?.applyBlockedFilter,
          isWeeklyStats: navigateToWeldProps?.isWeeklyStats,
        },
      });
    } else {
      navigate('/');
    }
  };

  const onSearch = (filter) => {
    setWeldFilter(filter);
  };

  return (
    <DashboardPersonaContentStyled key={crewMember?.id}>
      <FieldEngineerDashboardStatStyled data-testid="StatBlock-container">
        <ErrorBoundary>
          <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
            <CommodityStatsSection
              personaType={crewMember?.persona}
              crewMember={crewMember}
              navigateToWeld={navigateToWeld}
              weldTypeId={weldTypeId}
              error={fitupStatsError || weeklyCommodityStatsError}
              loading={fitupLoading || weeklyCommodityStatsLoading}
              fitupStatusStats={fitupStatusStats}
              refetch={fitUpRefetch || weeklyRefetch}
              weeklyStatusStats={weeklyStatusStats}
              weldFilter={weldFilter}
            />
          </Suspense>
        </ErrorBoundary>
      </FieldEngineerDashboardStatStyled>
      {isWideScreen ? (
        <WeldSectionListStyled key={crewMember?.id}>
          <FieldEngineerLeftSectionStyled>
            <SearchBar
              onSearch={onSearch}
              weldFilter={weldFilter}
              weldTypeId={weldTypeId}
              personaType={crewMember?.persona}
              crewMember={crewMember}
              showMap={showMap}
              toggleMap={setShowMap}
              enableProjectMap={enableProjectMap}
            />
            {showMap && enableProjectMap && (
              <MapContainerStyled key={crewMember?.id}>
                <MapMarkers weldFilter={weldFilter} crewMember={crewMember} />
              </MapContainerStyled>
            )}
            <WeldTabs
              personaType={crewMember?.persona}
              weldFilter={weldFilter}
              crewMember={crewMember}
              weldTypeId={weldTypeId}
              fitupStatusStats={fitupStatusStats}
              weeklyStatusStats={weeklyStatusStats}
              loading={fitupLoading || weeklyCommodityStatsLoading}
            />
          </FieldEngineerLeftSectionStyled>
        </WeldSectionListStyled>
      ) : (
        <Carousel key={crewMember?.id}>
          <CarouselContainerStyled>
            <SearchBar
              onSearch={onSearch}
              weldFilter={weldFilter}
              weldTypeId={weldTypeId}
              personaType={crewMember?.persona}
              crewMember={crewMember}
              showMap={showMap}
              toggleMap={setShowMap}
              enableProjectMap={enableProjectMap}
            />
            {showMap && enableProjectMap && (
              <MapContainerStyled style={{ height: '100%' }} key={crewMember?.id}>
                <MapMarkers weldFilter={weldFilter} crewMember={crewMember} />
              </MapContainerStyled>
            )}
          </CarouselContainerStyled>
          <CarouselContainerStyled>
            <SearchBar
              onSearch={onSearch}
              weldFilter={weldFilter}
              weldTypeId={weldTypeId}
              personaType={crewMember?.persona}
              crewMember={crewMember}
              showMap={showMap}
              toggleMap={setShowMap}
              enableProjectMap={enableProjectMap}
            />
            <WeldTabs
              personaType={crewMember?.persona}
              weldFilter={weldFilter}
              crewMember={crewMember}
              weldTypeId={weldTypeId}
              fitupStatusStats={fitupStatusStats}
              weeklyStatusStats={weeklyStatusStats}
              loading={fitupLoading || weeklyCommodityStatsLoading}
            />
          </CarouselContainerStyled>
        </Carousel>
      )}
    </DashboardPersonaContentStyled>
  );
};
